import type {
  CellClassRules,
  ColDef,
  ColGroupDef,
  ExcelExportParams,
  GetContextMenuItems,
  IAggFunc,
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { twMerge } from 'tailwind-merge';

import {
  expandGroups,
  formatIntegerToLocaleString,
  formatNonZeroNumberToLocaleString,
  formatToSimpleDate,
  formatValue,
  initialGroupOrderComparator,
  isAllEqualInArray,
  parseGermanPriceFormat,
  preciseAverageWithNull,
  preciseSum,
  preciseSumWithNull,
  toggleColumnGroups,
  valueFormatterNullableNumber,
  valueFormatterPercentage,
  valueFormatterZero,
  valueFormatterZeroWithNull,
} from '@org/utils';

import { ButtonCellRenderer } from '../ButtonCellRenderer';
import { CheckboxCellRenderer, CheckboxHeaderCellRenderer } from '../CheckboxCellRenderer';
import { MultipleButtonsRenderer } from '../MultipleButtonsRenderer';
import { NumericCellField } from '../NumericCellField';
import { SelectCellEditor } from '../SelectCellEditor';
import { TooltipCellRenderer } from '../TooltipCellRenderer';
import { defaultExcelStyles } from './utils/defaultExcelStyles';

import './ag-grid.css';

import { useTranslations } from 'next-intl';

import { DatePickerCellEditor } from '../DatePickerCellEditor';
import styles from './BaseTable.module.css';
import { esraTheme } from './theme';

const DEFAULT_SHEET_NAME = 'Daten';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface BaseTableProps<TData = any> extends AgGridReactProps<TData> {
  ref?: RefObject<AgGridReact | null>;

  isDisabled?: boolean;

  isEditable?: boolean;
}

type BaseTableComponent = <Data>(props: BaseTableProps<Data>) => React.JSX.Element | null;

const defaultColTypes = {
  date: {
    cellEditor: DatePickerCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    cellClass: 'ag-cell-type-date',
    valueFormatter: ({ value }) => (value ? formatToSimpleDate(new Date(value)) : ''),
    filter: 'agDateColumnFilter',
    filterValueGetter: (params) => params.data[params.colDef.field!],
    filterParams: {
      comparator: (valueA: string | null, valueB: string | null) => {
        if (valueA === null || valueB === null) {
          return 0;
        }
        const dateA = new Date(valueA);
        dateA.setHours(0, 0, 0, 0);
        const dateB = new Date(valueB);
        dateB.setHours(0, 0, 0, 0);

        return dateA.getTime() - dateB.getTime();
      },
      buttons: ['reset'],
    },
    valueSetter: (params) => {
      if (params.newValue && params.colDef.field) {
        params.data[params.colDef.field] = formatToSimpleDate(
          new Date(params.newValue),
          'yyyy-MM-dd',
        );
        return true;
      }
      return false;
    },
    valueGetter: (params) => {
      if (!params.data?.[params.colDef.field!]) {
        return false;
      }
      return new Date(params.data[params.colDef.field!]);
    },
  },
  float: {
    cellClass: 'ag-cell-type-float',
    valueFormatter: valueFormatterZero,
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  integer: {
    cellClass: 'ag-cell-type-integer',
    valueFormatter: ({ value }) =>
      value
        ? formatIntegerToLocaleString(value, {
            defaultValue: '-',
          })
        : '-',
    cellEditorParams: {
      decimalScale: 0,
    },
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  numberWithZero: {
    cellClass: 'ag-cell-type-number',
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  number: {
    cellClass: 'ag-cell-type-number',
    valueFormatter: valueFormatterZero,
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  nullableNumber: {
    cellClass: 'ag-cell-type-number',
    valueFormatter: valueFormatterNullableNumber,
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  percent: {
    cellClass: 'ag-cell-type-float',
    cellEditor: 'numeric',
    cellEditorParams: {
      decimalScale: 2,
    },
    valueFormatter: valueFormatterZeroWithNull,
    filter: 'agNumberColumnFilter',
  },
  percentAsInt: {
    cellClass: 'ag-cell-type-float',
    cellEditor: 'numeric',
    cellEditorParams: {
      decimalScale: 0,
    },
    valueFormatter: valueFormatterPercentage,
    filter: 'agNumberColumnFilter',
  },
  price: {
    cellClass: 'ag-cell-type-price',
    valueFormatter: valueFormatterZero,
    valueParser: ({ newValue }) => parseGermanPriceFormat(newValue),
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  'price-with-default-zero': {
    cellClass: 'ag-cell-type-price',
    valueFormatter: ({ value }) =>
      formatNonZeroNumberToLocaleString(value, { defaultValue: '0,00' }),
    valueParser: ({ newValue }) => parseGermanPriceFormat(newValue),
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  'report-price': {
    cellClass: ['ag-cell-type-report-price', 'accounting-number'],
    valueFormatter: valueFormatterZero,
    valueParser: ({ newValue }) => parseGermanPriceFormat(newValue),
    cellEditor: 'numeric',
    filter: 'agNumberColumnFilter',
  },
  select: {
    cellEditor: 'select',
    filter: 'agTextColumnFilter',
  },
  text: {
    cellClass: 'ag-cell-type-text',
    filter: 'agTextColumnFilter',
  },
  actions: {
    cellClass: 'ag-cell-type-actions',
  },
  comment: {
    cellRenderer: 'multipleButtons',
  },
} satisfies Record<string, ColDef>;

const customAggFunctions: Record<string, IAggFunc> = {
  preciseSum: ({ values }) => preciseSum(values),
  preciseSumWithNull: ({ values }) => preciseSumWithNull(values),
  sameValues: ({ values }) =>
    values.every((item, _, [firstItem]) => Object.is(item, firstItem)) ? values.at(0) : null,
  uniqueItems: ({ values }) => [...new Set(values)],
  preciseAverageWithNull: ({ values }) => preciseAverageWithNull(values),
  allEqualOrMixed: (params) => (isAllEqualInArray(params.values) ? params.values.at(0) : 'Mixed'),
};

// Workaround for jumping columns
const processUnpinnedColumns = () => [];

const Table = (properties: BaseTableProps) => {
  const {
    className,
    defaultColDef,
    excelStyles,
    defaultExcelExportParams: excelExportParameters,
    columnTypes,
    rowModelType,
    rowSelection,
    components,
    isDisabled,
    isEditable = true,
    ref,
    autoGroupColumnDef,
    getContextMenuItems: getContextMenuItemsProp,
    columnDefs,
    ...otherProperties
  } = properties;
  const t = useTranslations();
  const cellClassRules: CellClassRules = useMemo(
    () => ({
      'ag-editable': (params) => {
        if (!params.colDef.editable) {
          return false;
        }
        if (typeof params.colDef.editable === 'function') {
          return params.colDef.editable({ ...params });
        }
        return true;
      },
      ...defaultColDef?.cellClassRules,
    }),
    [defaultColDef?.cellClassRules],
  );

  const combinedExcelStyles = useMemo(
    () => [...defaultExcelStyles, ...(excelStyles ?? [])],
    [excelStyles],
  );

  const combinedDefaultExcelExportParameters = useMemo(
    () =>
      ({
        sheetName: DEFAULT_SHEET_NAME,
        ...excelExportParameters,
      }) satisfies ExcelExportParams,
    [excelExportParameters],
  );

  const defaultColDefCombined: ColDef = useMemo(
    () => ({
      singleClickEdit: true,
      suppressHeaderMenuButton: true,
      autoHeaderHeight: true,
      resizable: true,
      sortable: true,
      suppressMovable: true,
      tooltipComponent: TooltipCellRenderer,
      tooltipValueGetter: (parameters) => formatValue(parameters.value),
      type: 'text',
      useValueFormatterForExport: true,
      ...defaultColDef,
      cellClassRules,
      editable: isEditable ? defaultColDef?.editable : false,
    }),
    [cellClassRules, defaultColDef, isEditable],
  );

  const notServerSideProps = useMemo(() => {
    if (rowModelType !== 'serverSide') {
      return {
        // Black magic for ordering group rows
        initialGroupOrderComparator,
      };
    }
  }, [rowModelType]);

  const getContextMenuItems: GetContextMenuItems = useCallback(
    (args) => {
      if (getContextMenuItemsProp) {
        return getContextMenuItemsProp(args);
      }
      const { api } = args;
      let maxLevel = 0;
      api.forEachNode((node) => {
        if (node.level > maxLevel) {
          maxLevel = node.level;
        }
      });

      return [
        {
          name: t('common.expandColumns'),
          action: () => {
            toggleColumnGroups({ current: args }, true);
          },
        },
        {
          name: t('common.collapseColumns'),
          action: () => {
            toggleColumnGroups({ current: args }, false);
          },
        },
        {
          name: t('common.expandRows'),
          subMenu: Array.from({ length: maxLevel + 1 }, (_, i) => ({
            name: t('common.level', { level: i + 1 }),
            action: () => expandGroups({ current: args }, i - 1),
          })),
        },
        ...(args.defaultItems ?? []),
      ];
    },
    [getContextMenuItemsProp, t],
  );

  const autoGroupColumn = useMemo(() => {
    if (autoGroupColumnDef) {
      return {
        ...autoGroupColumnDef,
        editable: isEditable ? (autoGroupColumnDef.editable ?? defaultColDef?.editable) : false,
        rowDrag: isEditable ? (autoGroupColumnDef.rowDrag ?? defaultColDef?.rowDrag) : false,
      };
    }
    return undefined;
  }, [autoGroupColumnDef, defaultColDef?.editable, defaultColDef?.rowDrag, isEditable]);

  const disableEditColumns = useCallback(
    (columns: (ColDef | ColGroupDef)[]) =>
      columns.map((item): ColDef | ColGroupDef => {
        if ('children' in item) {
          return {
            ...item,
            // eslint-disable-next-line react-hooks/immutability
            children: disableEditColumns(item.children),
          };
        }
        if (item.type === 'actions') {
          return {
            ...item,
            editable: false,
            hide: !isEditable,
          };
        }
        return {
          ...item,
          editable: isEditable ? item.editable : false,
        };
      }),
    [isEditable],
  );

  const columns = useMemo(
    () => disableEditColumns(columnDefs ?? []),
    [disableEditColumns, columnDefs],
  );

  return (
    <div className={twMerge('ag-grid', isDisabled && 'ag-disabled', styles.BaseTable, className)}>
      <AgGridReact
        aggFuncs={customAggFunctions}
        animateRows
        columnTypes={useMemo(
          () => ({
            ...defaultColTypes,
            ...columnTypes,
          }),
          [columnTypes],
        )}
        components={useMemo(
          () => ({
            ...components,
            button: ButtonCellRenderer,
            checkbox: CheckboxCellRenderer,
            headerCheckbox: CheckboxHeaderCellRenderer,
            multipleButtons: MultipleButtonsRenderer,
            numeric: NumericCellField,
            select: SelectCellEditor,
          }),
          [components],
        )}
        defaultColDef={defaultColDefCombined}
        defaultExcelExportParams={combinedDefaultExcelExportParameters}
        enableCellTextSelection
        excelStyles={combinedExcelStyles}
        getContextMenuItems={getContextMenuItems}
        groupAllowUnbalanced
        groupHeaderHeight={23}
        headerHeight={23}
        processUnpinnedColumns={processUnpinnedColumns}
        ref={ref}
        rowModelType={rowModelType}
        rowSelection={useMemo(() => {
          if (!rowSelection || typeof rowSelection === 'string') {
            return rowSelection;
          }
          return {
            suppressRowClickSelection: true,
            ...(rowSelection.mode === 'multiRow' && { selectAll: 'filtered' }),
            ...rowSelection,
          };
        }, [rowSelection])}
        stopEditingWhenCellsLoseFocus
        suppressAggFuncInHeader
        suppressFieldDotNotation
        theme={esraTheme}
        {...notServerSideProps}
        {...otherProperties}
        autoGroupColumnDef={autoGroupColumn}
        className="h-full"
        columnDefs={columns}
      />
    </div>
  );
};
Table.displayName = 'BaseTable';

export const BaseTable = Table as BaseTableComponent;
